import { Button, DatePicker, Drawer, Input, InputNumber, Radio, Select, Spin, Typography } from "@arco-design/web-react";
import {
  ChevronDown,
  ChevronRight,
  CopyPlus,
  GitCommitHorizontal,
  LoaderCircle,
  MoreHorizontal,
  Route,
  SendHorizontal,
} from "lucide-react";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import type { ReactElement, ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";

import { useCurrentAuthUser } from "../../auth";
import type { AuthUser } from "../../auth";
import { ITINERARY_PERMISSIONS } from "../../itinerary";
import type {
  CustomizationDemandDisplayField,
  CustomizationDemandDisplayGroup,
  CustomizationDemandFieldOptions,
  CustomizationProjectEditOptions,
  CreateCustomizationDemandResult,
  CustomizationProjectFieldOptions,
  CustomizationDemandVersionOption,
  CustomizationProjectUpdatePayload,
  CustomizationOpportunityBinding,
  CustomizationOpportunityBindingDecision,
} from "../../customization";
import { CustomizationOpportunityBindingContent } from "../../customization";
import { CustomizationQuickItineraryDetails, type QuickItineraryDetailsEditor } from "../../customization";
import {
  DispatcherSelectionField,
  DispatcherSelectionModal,
  usePrefetchDispatcherSelectionCandidates,
} from "../../customization/ui/DispatcherSelectionModal";
import type { CustomizationDispatcherSelectionCandidate } from "../../customization";
import type { CustomizationMobileWorkbenchViewModel } from "../types";
import { canUsePermission } from "../../../lib/auth";
import { getProvinceOptions } from "../../../shared/region";

import "./CustomizationMobileWorkbenchPage.css";

const { Text, Title } = Typography;

type MobileEditFieldKey =
  | "primarySchoolId"
  | "customerId"
  | "contactId"
  | "provinces"
  | "tripType"
  | "signType"
  | "dealProgress"
  | "customerType"
  | "dispatcherUserId"
  | "operatingCompany"
  | "departureStartDate"
  | "durationDays"
  | "studentCount"
  | "teacherCount"
  | "totalCount"
  | "destinationSummary"
  | "budgetPerPerson"
  | "majorNames"
  | "pointNames"
  | "stayAreaNames"
  | "teachingRequirements"
  | "specialRequirements"
  | "schoolPickupRequirement"
  | "invoiceRequirement"
  | "remark"
  | "teacherMealRequirement"
  | "teacherTicketRequirement"
  | "teacherTransportRequirement"
  | "teacherAccommodationRequirement"
  | "roundTripTransportNote"
  | "localTransportNote"
  | "accommodationNote"
  | "guideCount"
  | "guideNote"
  | "includeTickets"
  | "ticketNote"
  | "includeMeals"
  | "mealStandards"
  | "mealNote"
  | "companyVisitCount"
  | "companyVisitNote"
  | "intangibleHeritageCount"
  | "intangibleHeritageNote";

const mobileEditFieldLabels: Record<MobileEditFieldKey, string> = {
  primarySchoolId: "高校",
  customerId: "客户",
  contactId: "联系人",
  provinces: "目的地省",
  tripType: "出行类型",
  signType: "签单类型",
  dealProgress: "成单进度",
  customerType: "客户类型",
  dispatcherUserId: "第一计调",
  operatingCompany: "操作公司",
  departureStartDate: "出发日期",
  durationDays: "行程天数",
  studentCount: "出行人数",
  teacherCount: "老师人数",
  totalCount: "总人数",
  destinationSummary: "目的地",
  budgetPerPerson: "人均预算区间",
  majorNames: "专业",
  pointNames: "考察点",
  stayAreaNames: "目的地住宿",
  teachingRequirements: "教学要求",
  specialRequirements: "特殊需求",
  schoolPickupRequirement: "接送要求",
  invoiceRequirement: "发票要求",
  remark: "备注",
  teacherMealRequirement: "老师餐饮要求",
  teacherTicketRequirement: "老师门票要求",
  teacherTransportRequirement: "老师交通要求",
  teacherAccommodationRequirement: "老师住宿要求",
  roundTripTransportNote: "行程大交通工具",
  localTransportNote: "大巴车",
  accommodationNote: "团队住宿",
  guideCount: "领队/导游数",
  guideNote: "领队/导游要求",
  includeTickets: "是否含考察点门票",
  ticketNote: "考察点门票要求",
  includeMeals: "全程是否含餐",
  mealStandards: "团队餐饮类型",
  mealNote: "团队餐饮要求",
  companyVisitCount: "考察企业数",
  companyVisitNote: "考察企业要求",
  intangibleHeritageCount: "考察非遗数",
  intangibleHeritageNote: "考察非遗要求",
};

const mobileNumberEditFields = new Set<MobileEditFieldKey>([
  "durationDays",
  "studentCount",
  "teacherCount",
  "totalCount",
  "guideCount",
  "companyVisitCount",
  "intangibleHeritageCount",
]);

const mobileArrayEditFields = new Set<MobileEditFieldKey>([
  "provinces",
  "majorNames",
  "pointNames",
  "stayAreaNames",
  "mealStandards",
]);

const mobileBooleanEditFields = new Set<MobileEditFieldKey>(["includeTickets", "includeMeals"]);
const fallbackMobileSchoolPickupRequirementOptions = ["不接送", "单接", "单送", "接送"];

const mobileTextAreaEditFields = new Set<MobileEditFieldKey>([
  "teachingRequirements",
  "specialRequirements",
  "invoiceRequirement",
  "remark",
  "teacherMealRequirement",
  "teacherTicketRequirement",
  "teacherTransportRequirement",
  "teacherAccommodationRequirement",
  "roundTripTransportNote",
  "localTransportNote",
  "accommodationNote",
  "guideNote",
  "ticketNote",
  "mealNote",
  "companyVisitNote",
  "intangibleHeritageNote",
]);

const mobileCompactDemandFieldKeys = new Set([
  "departureStartDate",
  "durationDays",
  "studentCount",
  "teacherCount",
]);

const mobileDispatcherDemandFieldKeys = new Set(["dispatcherUserName"]);

type MobileNativeSelectOption = {
  value: string;
  label: string;
};

type MobileNameSelectOptionInput = MobileNativeSelectOption | string;
type MobileDemandDisplayField = CustomizationDemandDisplayField & {
  missing?: boolean;
  required?: boolean;
};

const emptyMobileIdentityOptions: MobileNativeSelectOption[] = [];
const emptyMobileNameOptions: MobileNameSelectOptionInput[] = [];
const mobileProjectEditProvinceOptions = getProvinceOptions();

type Props = {
  quickItinerary?: QuickItineraryDetailsEditor;
  confirmedTraceCount: number;
  createDemandVersion: () => Promise<CreateCustomizationDemandResult | null>;
  createNewCustomization: () => Promise<CreateCustomizationDemandResult | null | void>;
  itineraryPagePath: string;
  notifyDispatcher: (actionText?: string) => Promise<void>;
  saveProject: (payload: CustomizationProjectUpdatePayload) => Promise<unknown>;
  selectVersion: (versionId: string) => Promise<void>;
  startItineraryGeneration: () => Promise<boolean>;
  closeOpportunityBinding: () => void;
  resolveOpportunityBinding: (
    decision: CustomizationOpportunityBindingDecision,
  ) => Promise<CustomizationOpportunityBinding | null>;
  viewModel: CustomizationMobileWorkbenchViewModel;
};

type CustomizationMobileLocalNotice = {
  title: string;
  message: string;
  type: "error" | "success";
};

type MobileDemandFieldFilter = "all" | "required" | "draft" | "version";

/** 手机临时版需求定制页面。 */
export function CustomizationMobileWorkbenchFeaturePage({
  quickItinerary,
  confirmedTraceCount,
  createDemandVersion,
  createNewCustomization,
  itineraryPagePath,
  notifyDispatcher,
  saveProject,
  selectVersion,
  startItineraryGeneration,
  closeOpportunityBinding,
  resolveOpportunityBinding,
  viewModel,
}: Props) {
  const navigate = useNavigate();
  const currentUserQuery = useCurrentAuthUser();
  const canCreateProject = canUsePermission(currentUserQuery.data, "customization.project:create");
  const canUpdateProject = canUsePermission(currentUserQuery.data, "customization.project:update");
  const canGenerateDemand = canUsePermission(
    currentUserQuery.data,
    "customization.project:generate",
  );
  const canBindOpportunity = canUsePermission(
    currentUserQuery.data,
    "customization.project:bind_opportunity",
  );
  const canNotifyDispatcher = canUsePermission(
    currentUserQuery.data,
    "customization.project:notify",
  );
  const canGenerateItinerary = canUsePermission(
    currentUserQuery.data,
    ITINERARY_PERMISSIONS.generate,
  );
  const canCreateOpportunity = canUsePermission(currentUserQuery.data, "crm.opportunity:access");
  const [versionDrawerVisible, setVersionDrawerVisible] = useState(false);
  const [singleEditFields, setSingleEditFields] = useState<MobileEditFieldKey[] | null>(null);
  const [singleEditTitle, setSingleEditTitle] = useState("");
  const [singleEditDraft, setSingleEditDraft] = useState<CustomizationProjectUpdatePayload>({});
  const [pendingVersionId, setPendingVersionId] = useState<string | null>(null);
  const [localNotice, setLocalNotice] = useState<CustomizationMobileLocalNotice | null>(null);
  const [notifyConfirmVisible, setNotifyConfirmVisible] = useState(false);
  const [notifyActionText, setNotifyActionText] = useState("");
  const [historicalBaseConfirmVisible, setHistoricalBaseConfirmVisible] = useState(false);
  const [createNewConfirmVisible, setCreateNewConfirmVisible] = useState(false);
  const [moreActionsVisible, setMoreActionsVisible] = useState(false);
  const [dispatcherSelectionVisible, setDispatcherSelectionVisible] = useState(false);
  const [dispatcherSelectionInitial, setDispatcherSelectionInitial] = useState<{
    userId?: string;
    displayName?: string;
  }>({});
  const dispatcherSelectionResolverRef = useRef<
    ((candidate: CustomizationDispatcherSelectionCandidate | null) => void) | null
  >(null);
  const [fieldFilter, setFieldFilter] = useState<MobileDemandFieldFilter>("all");
  usePrefetchDispatcherSelectionCandidates(Boolean(viewModel.projectId));
  const switchingStatusRef = useRef<HTMLDivElement>(null);
  const versionSwitchButtonRef = useRef<HTMLButtonElement>(null);
  const wasVersionSwitchingRef = useRef(false);
  const notifyDispatcherLabel = formatNotifyDispatcherLabel(viewModel.dispatcherName);
  const currentUserRealDisplayName = resolveMobileNotificationActorName(currentUserQuery.data);
  const notifyActionOptions = useMemo(
    () =>
      buildMobileNotifyActionOptions({
        actorName: currentUserRealDisplayName,
        versionNumber: viewModel.currentVersion?.versionNumber,
      }),
    [currentUserRealDisplayName, viewModel.currentVersion?.versionNumber],
  );
  const filteredDemandGroups = useMemo(
    () =>
      filterMobileDemandGroups(
        viewModel.demandGroups,
        fieldFilter,
        viewModel.manualDraftPatch,
        viewModel.versionChangedFieldKeys,
      ),
    [fieldFilter, viewModel.demandGroups, viewModel.manualDraftPatch, viewModel.versionChangedFieldKeys],
  );
  const hasUnsubmittedChanges = viewModel.hasManualDraft || confirmedTraceCount > 0;
  const canSubmitDemandVersion =
    canGenerateDemand && (!viewModel.hasManualDraft || canUpdateProject);
  const canCreateNewCustomization =
    canCreateProject && (!viewModel.hasManualDraft || canUpdateProject);
  const postVersionActionsDisabled =
    viewModel.persistedRequiredStatus.status !== "complete" || hasUnsubmittedChanges;
  const postVersionActionsDisabledReason = resolveMobilePostVersionDisabledReason({
    confirmedTraceCount,
    hasManualDraft: viewModel.hasManualDraft,
    requiredStatusText: viewModel.persistedRequiredStatus.text,
    requiredStatus: viewModel.persistedRequiredStatus.status,
  });
  // 通知计调允许必填未齐（后端 keyword4 附未齐备项）；仅草稿未提交时拦截。
  const notifyBlockedByUnsubmittedChanges = hasUnsubmittedChanges;
  const notifyBlockedReason = notifyBlockedByUnsubmittedChanges
    ? "请先提交当前草稿和已确认追溯，再生成行程或通知计调。"
    : "";

  useEffect(() => {
    if (viewModel.versionSwitching) {
      switchingStatusRef.current?.focus();
    } else if (wasVersionSwitchingRef.current) {
      versionSwitchButtonRef.current?.focus();
    }
    wasVersionSwitchingRef.current = viewModel.versionSwitching;
  }, [viewModel.versionSwitching]);

  useEffect(() => {
    if (!viewModel.errorMessage) {
      return;
    }
    setLocalNotice({
      message: viewModel.errorMessage,
      title: viewModel.versionSubmitLabel.startsWith("更新") ? "更新失败" : "创建失败",
      type: "error",
    });
  }, [viewModel.errorMessage, viewModel.versionSubmitLabel]);

  useEffect(() => {
    if (!viewModel.notice) {
      return;
    }
    setLocalNotice({
      message: viewModel.notice.message,
      title: viewModel.notice.title,
      type:
        viewModel.notice.title === "通知失败" || viewModel.notice.title === "请填写计调人员"
          ? "error"
          : "success",
    });
  }, [viewModel.notice]);

  useEffect(() => {
    if (!localNotice) {
      return;
    }
    const timer = window.setTimeout(() => setLocalNotice(null), 2400);
    return () => window.clearTimeout(timer);
  }, [localNotice]);

  const handleOpenSingleEditor = (field: CustomizationDemandDisplayField) => {
    const fields = getMobileDemandFieldEditKeys(field.key, {
      disableDispatcherEdit: viewModel.disableDispatcherEdit,
    });
    if (fields.length === 0) {
      return;
    }
    if (fields.length === 1 && fields[0] === "dispatcherUserId") {
      openDispatcherSelection({
        selectedUserId: viewModel.editModel.dispatcherUserId,
        selectedDisplayName: viewModel.editModel.dispatcherUserName,
        onConfirm: async (candidate) => {
          const result = await saveProject(
            candidate
              ? {
                  dispatcherUserId: candidate.userId,
                  dispatcherUserName: candidate.displayName,
                }
              : {
                  dispatcherUserId: "",
                  dispatcherUserName: "",
                },
          );
          if (!result) {
            return;
          }
          setSingleEditFields(null);
          setSingleEditTitle("");
          setSingleEditDraft({});
        },
      });
      return;
    }
    setSingleEditFields(fields);
    setSingleEditTitle(field.label);
    setSingleEditDraft(pickMobileEditFields(viewModel.editModel, fields));
  };

  const openDispatcherSelection = ({
    selectedUserId,
    selectedDisplayName,
    onConfirm,
  }: {
    selectedUserId?: string;
    selectedDisplayName?: string;
    onConfirm: (candidate: CustomizationDispatcherSelectionCandidate | null) => void;
  }) => {
    setDispatcherSelectionInitial({
      userId: selectedUserId,
      displayName: selectedDisplayName,
    });
    dispatcherSelectionResolverRef.current = onConfirm;
    setDispatcherSelectionVisible(true);
  };

  const handleDispatcherSelectionClose = () => {
    dispatcherSelectionResolverRef.current = null;
    setDispatcherSelectionInitial({});
    setDispatcherSelectionVisible(false);
  };

  const handleDispatcherSelectionConfirm = (
    candidate: CustomizationDispatcherSelectionCandidate | null,
  ) => {
    void dispatcherSelectionResolverRef.current?.(candidate);
    dispatcherSelectionResolverRef.current = null;
    setDispatcherSelectionInitial({});
    setDispatcherSelectionVisible(false);
  };

  const handleSaveSingleField = async (draft: CustomizationProjectUpdatePayload) => {
    if (!singleEditFields) {
      return;
    }
    const result = await saveProject(
      normalizeMobileEditPayload(pickMobileEditFields(draft, singleEditFields)),
    );
    if (result) {
      setSingleEditFields(null);
      setSingleEditTitle("");
      setSingleEditDraft({});
    }
  };

  const handleVersionRowClick = (versionId: string) => {
    if (versionId === viewModel.selectedVersionId) {
      setVersionDrawerVisible(false);
      return;
    }
    if (viewModel.hasManualDraft) {
      setPendingVersionId(versionId);
      setVersionDrawerVisible(false);
      return;
    }
    setVersionDrawerVisible(false);
    void selectVersion(versionId);
  };

  const handleConfirmVersionSwitch = () => {
    const versionId = pendingVersionId;
    setPendingVersionId(null);
    if (versionId) {
      void selectVersion(versionId);
    }
  };

  const handleNotifyDispatcherClick = () => {
    if (!viewModel.dispatcherUserId?.trim()) {
      void notifyDispatcher();
      return;
    }
    if (notifyBlockedByUnsubmittedChanges) {
      setLocalNotice({
        message: notifyBlockedReason,
        title: "通知未发送",
        type: "error",
      });
      return;
    }
    setLocalNotice(null);
    setNotifyActionText(notifyActionOptions[0]?.value ?? "");
    setNotifyConfirmVisible(true);
  };

  const handleConfirmNotifyDispatcher = async () => {
    await notifyDispatcher(notifyActionText);
    setNotifyConfirmVisible(false);
  };

  const handleConfirmCreateNewCustomization = async () => {
    const result = await createNewCustomization();
    if (result) {
      setCreateNewConfirmVisible(false);
    }
  };

  const handleItineraryAction = async () => {
    setMoreActionsVisible(false);
    if (viewModel.currentVersion?.hasItinerary) {
      navigate(itineraryPagePath);
      return;
    }
    const started = await startItineraryGeneration();
    if (started) {
      navigate(itineraryPagePath);
    }
  };

  const submitDemandVersion = async () => {
    const result = await createDemandVersion();
    if (!result) {
      return;
    }
    setLocalNotice({
      message: buildMobileVersionSuccessMessage(
        viewModel.versionSubmitLabel,
        result.demand.versionNumber,
      ),
      title: "操作成功",
      type: "success",
    });
  };

  const handleCreateDemandVersion = async () => {
    if (viewModel.shouldConfirmHistoricalBaseGeneration) {
      setHistoricalBaseConfirmVisible(true);
      return;
    }
    await submitDemandVersion();
  };

  const handleConfirmHistoricalBaseGeneration = async () => {
    setHistoricalBaseConfirmVisible(false);
    await submitDemandVersion();
  };

  if (viewModel.loading) {
    return (
      <main className="customization-mobile-workbench-page" aria-label="手机临时需求定制页">
        <div
          className="customization-mobile-loading"
          role="status"
          aria-live="polite"
          aria-atomic="true"
        >
          <Spin dot />
          <span>加载中...</span>
        </div>
      </main>
    );
  }

  return (
    <main
      className="customization-mobile-workbench-page"
      aria-label="手机临时需求定制页"
      aria-busy={viewModel.versionSwitching || undefined}
    >
      <div
        className="customization-mobile-workbench-content customization-mobile-page-scroll"
        inert={viewModel.versionSwitching ? true : undefined}
      >
      <section className="customization-mobile-hero" aria-label="手机需求定制项目概览">
        <div className="customization-mobile-project-title-row">
          <Title heading={4} className="customization-mobile-title">
            {viewModel.projectName}
          </Title>
          <button
            ref={versionSwitchButtonRef}
            className="customization-mobile-version-trigger"
            type="button"
            aria-label="切换版本"
            disabled={viewModel.versions.length === 0}
            onClick={() => setVersionDrawerVisible(true)}
          >
            <span>{viewModel.currentVersion?.versionNumber ?? "未生成"}</span>
            <ChevronDown size={15} />
          </button>
        </div>
        <Text className="customization-mobile-meta">{viewModel.projectCode}</Text>
        <div className="customization-mobile-owner-row" aria-label="项目人员">
          <span className="customization-mobile-owner-tag"><strong>销售</strong> {viewModel.salesName}</span>
          <span className="customization-mobile-owner-tag is-dispatcher"><strong>计调</strong> {viewModel.dispatcherName}</span>
        </div>
        <div className="customization-mobile-project-status-line">
          <span className={`customization-mobile-mini-status is-${viewModel.requiredStatus.status}`}>
            {viewModel.requiredStatus.title}
          </span>
          <Link className="customization-mobile-itinerary-link" to={itineraryPagePath}>
            <Route size={15} />
            <span>
              {viewModel.currentVersion?.hasItinerary
                ? `查看 ${viewModel.currentVersion.versionNumber} 行程`
                : `${viewModel.currentVersion?.versionNumber ?? "当前版本"} 暂无行程`}
            </span>
          </Link>
        </div>
      </section>

      <section className="customization-mobile-field-filters" aria-label="需求字段筛选">
        <Radio.Group
          type="button"
          value={fieldFilter}
          onChange={(value) => setFieldFilter(value as MobileDemandFieldFilter)}
        >
          <Radio value="all">全部</Radio>
          <Radio value="required">只看必填项</Radio>
          <Radio value="draft">只看已修改</Radio>
          {viewModel.versionSeq > 1 ? <Radio value="version">本版变化</Radio> : null}
        </Radio.Group>
      </section>

      <section className="customization-mobile-demand-groups" aria-label="手机需求字段">
        {filteredDemandGroups.length > 0 ? (
          filteredDemandGroups.map((group) => (
            <MobileDemandGroup
              key={group.key}
              canEdit={canUpdateProject}
              disableDispatcherEdit={viewModel.disableDispatcherEdit}
              editModel={viewModel.editModel}
              group={group}
              onEditField={handleOpenSingleEditor}
              onInlineNumberSave={saveProject}
            />
          ))
        ) : (
          <div className="customization-mobile-empty">
            <strong>{fieldFilter === "all" ? "暂无结构化需求" : "当前筛选暂无字段"}</strong>
            <span>{fieldFilter === "all" ? "当前项目还没有生成需求定制版本。" : "切换筛选可查看其他需求字段。"}</span>
          </div>
        )}
      </section>
      {quickItinerary ? <CustomizationQuickItineraryDetails editor={quickItinerary} /> : null}
      </div>

      <section className="customization-mobile-confirmation-bar" aria-label="手机需求定制操作区">
        <div className="customization-mobile-confirmation-status">
          <div className="customization-mobile-confirmation-tags">
            <span className={`is-${viewModel.requiredStatus.status}`}>
              {viewModel.currentVersion?.versionNumber ?? "未生成"}
            </span>
            {confirmedTraceCount > 0 ? <span className="is-trace">已选追溯 {confirmedTraceCount}</span> : null}
            {viewModel.manualDraftFieldCount > 0 ? (
              <span className="is-draft">草稿 {viewModel.manualDraftFieldCount} 项</span>
            ) : null}
          </div>
        </div>
        <p className="customization-mobile-confirmation-hint">
          {resolveMobileConfirmationStatus(viewModel, confirmedTraceCount)}
        </p>
        <div className="customization-mobile-confirmation-actions">
          <Button
            className="customization-mobile-version-submit-button"
            disabled={!canSubmitDemandVersion || viewModel.versionSubmitting || viewModel.versionSubmitDisabled}
            icon={<GitCommitHorizontal size={17} />}
            loading={viewModel.versionSubmitting}
            type="primary"
            onClick={() => {
              void handleCreateDemandVersion();
            }}
          >
            {viewModel.versionSubmitLabel}
          </Button>
          <Button
            className="customization-mobile-create-new-button"
            disabled={!canCreateNewCustomization || !viewModel.displayDemandId || viewModel.versionSubmitting}
            icon={<CopyPlus size={17} />}
            type="secondary"
            onClick={() => setCreateNewConfirmVisible(true)}
          >
            复制，创建新定制
          </Button>
          <Button
            className="customization-mobile-more-button"
            aria-label="更多操作"
            icon={<MoreHorizontal size={19} />}
            disabled={!viewModel.displayDemandId}
            type="secondary"
            onClick={() => setMoreActionsVisible(true)}
          />
        </div>
      </section>

      {localNotice ? (
        <div
          className={`customization-mobile-toast is-${localNotice.type}`}
          role={localNotice.type === "error" ? "alert" : "status"}
          aria-live="polite"
        >
          <strong>{localNotice.title}</strong>
          <span>{localNotice.message}</span>
        </div>
      ) : null}

      {viewModel.versionSwitching ? (
        <div
          ref={switchingStatusRef}
          className="customization-mobile-switching"
          role="status"
          aria-label="版本切换过渡"
          aria-live="polite"
          aria-atomic="true"
          tabIndex={-1}
        >
          <div className="customization-mobile-switching-card">
            <LoaderCircle size={18} />
            <strong>正在切换版本</strong>
            <span>正在加载历史需求，请稍候</span>
          </div>
        </div>
      ) : null}

      <Drawer
        className="customization-mobile-opportunity-drawer"
        title={viewModel.opportunityBinding?.status === "review_required" ? "复核商机归属" : "确认商机归属"}
        placement="bottom"
        height="min(88vh, 720px)"
        footer={null}
        visible={Boolean(viewModel.opportunityBindingOpen && viewModel.opportunityBinding)}
        maskClosable={false}
        unmountOnExit
        onCancel={closeOpportunityBinding}
      >
        {viewModel.opportunityBinding ? (
          <CustomizationOpportunityBindingContent
            binding={viewModel.opportunityBinding}
            candidatesLoading={viewModel.opportunityBindingPreparing}
            canBindOpportunity={canBindOpportunity}
            canCreateOpportunity={canCreateOpportunity}
            onResolve={resolveOpportunityBinding}
            onClose={closeOpportunityBinding}
          />
        ) : null}
      </Drawer>
      <Drawer
        className="customization-mobile-version-drawer"
        title="切换需求版本"
        placement="bottom"
        height="min(76vh, 520px)"
        footer={null}
        visible={versionDrawerVisible}
        unmountOnExit
        onCancel={() => setVersionDrawerVisible(false)}
      >
        <div className="customization-mobile-version-list" aria-label="手机需求版本列表">
          {viewModel.versions.map((version) => (
            <button
              key={version.id}
              className={`customization-mobile-version-row${
                version.id === viewModel.selectedVersionId ? " is-selected" : ""
              }`}
              type="button"
              aria-label={`查看 ${version.versionNumber}`}
              onClick={() => handleVersionRowClick(version.id)}
            >
              <span className="customization-mobile-version-row-main">
                <strong>{version.versionNumber}</strong>
                {version.isCurrent ? <em>当前</em> : null}
              </span>
              <span className="customization-mobile-version-row-status">
                {formatRequiredStatus(version)}
                <span>{version.hasItinerary ? "已有行程" : "暂无行程"}</span>
              </span>
              <ChevronRight size={16} />
            </button>
          ))}
        </div>
      </Drawer>

      <Drawer
        className="customization-mobile-confirm-drawer"
        title="切换版本会放弃当前未提交修改"
        placement="bottom"
        height="auto"
        visible={Boolean(pendingVersionId)}
        unmountOnExit
        footer={
          <div className="customization-mobile-drawer-actions">
            <Button onClick={() => setPendingVersionId(null)}>取消</Button>
            <Button type="primary" onClick={handleConfirmVersionSwitch}>
              确认放弃并切换
            </Button>
          </div>
        }
        onCancel={() => setPendingVersionId(null)}
      >
        <div className="customization-mobile-confirm-body">
          <p>当前手机端字段修改尚未创建为新版本，切换后这些本地修改会被放弃。</p>
        </div>
      </Drawer>

      <Drawer
        className="customization-mobile-confirm-drawer"
        title="创建新定制"
        placement="bottom"
        height="auto"
        footer={null}
        visible={createNewConfirmVisible}
        unmountOnExit
        onCancel={() => setCreateNewConfirmVisible(false)}
      >
        <div className="customization-mobile-create-new-confirm" aria-label="创建新定制确认">
          <p>来源项目：{viewModel.projectName}</p>
          <p>当前可见版本：{viewModel.currentVersion?.versionNumber ?? "未生成"}</p>
          <p>已确认追溯 {confirmedTraceCount} 条</p>
          <p>
            手工草稿 {viewModel.manualDraftFieldCount} 项
            {viewModel.manualDraftFieldCount > 0 ? "，将一起带入" : ""}
          </p>
          <strong>新项目将从 V1 开始</strong>
          <div className="customization-mobile-drawer-actions">
            <Button onClick={() => setCreateNewConfirmVisible(false)}>取消</Button>
            <Button
              type="primary"
              loading={viewModel.versionSubmitting}
              disabled={!canCreateNewCustomization || viewModel.versionSubmitting}
              onClick={() => {
                void handleConfirmCreateNewCustomization();
              }}
            >
              确认创建
            </Button>
          </div>
        </div>
      </Drawer>

      <Drawer
        className="customization-mobile-more-drawer"
        title="更多操作"
        placement="bottom"
        height="auto"
        footer={null}
        visible={moreActionsVisible}
        unmountOnExit
        onCancel={() => setMoreActionsVisible(false)}
      >
        <div className="customization-mobile-more-actions" aria-label="手机更多操作面板">
          {viewModel.currentVersion?.hasItinerary || canGenerateItinerary ? (
            <button
              className="customization-mobile-sheet-action"
              type="button"
              aria-label={viewModel.currentVersion?.hasItinerary ? "查看行程" : "智能生成行程"}
              disabled={!viewModel.currentVersion?.hasItinerary && (!canGenerateItinerary || postVersionActionsDisabled)}
              onClick={() => {
                void handleItineraryAction();
              }}
            >
              <span className="customization-mobile-sheet-action-icon"><Route size={17} /></span>
              <span className="customization-mobile-sheet-action-copy">
                <strong>{viewModel.currentVersion?.hasItinerary ? "查看行程" : "智能生成行程"}</strong>
                <span>
                  {!viewModel.currentVersion?.hasItinerary && postVersionActionsDisabledReason
                    ? postVersionActionsDisabledReason
                    : `进入 ${viewModel.currentVersion?.versionNumber ?? "当前版本"} 行程计划`}
                </span>
              </span>
              <ChevronRight size={16} />
            </button>
          ) : null}
          <button
            className="customization-mobile-sheet-action"
            type="button"
            aria-label={notifyDispatcherLabel}
            disabled={!canNotifyDispatcher || notifyBlockedByUnsubmittedChanges || !viewModel.displayDemandId || viewModel.notifying}
            onClick={() => {
              setMoreActionsVisible(false);
              handleNotifyDispatcherClick();
            }}
          >
            <span className="customization-mobile-sheet-action-icon"><SendHorizontal size={17} /></span>
            <span className="customization-mobile-sheet-action-copy">
              <strong>{notifyDispatcherLabel}</strong>
              <span>{notifyBlockedReason || "发送当前已保存需求版本"}</span>
            </span>
            <ChevronRight size={16} />
          </button>
        </div>
      </Drawer>

      <Drawer
        className="customization-mobile-notify-drawer"
        title="通知计调"
        placement="bottom"
        height="auto"
        visible={notifyConfirmVisible}
        unmountOnExit
        footer={
          <div className="customization-mobile-drawer-actions">
            <Button onClick={() => setNotifyConfirmVisible(false)}>取消</Button>
            <Button
              type="primary"
              disabled={!notifyActionText.trim() || viewModel.notifying}
              loading={viewModel.notifying}
              onClick={handleConfirmNotifyDispatcher}
            >
              确认通知
            </Button>
          </div>
        }
        onCancel={() => setNotifyConfirmVisible(false)}
      >
        <div className="customization-mobile-notify-form" aria-label="手机通知计调确认表单">
          <p>通知对象：{viewModel.dispatcherName}</p>
          <p>当前版本：{viewModel.currentVersion?.versionNumber ?? "未生成"}</p>
          <Radio.Group
            direction="vertical"
            value={notifyActionText}
            onChange={(value) => setNotifyActionText(String(value))}
          >
            {notifyActionOptions.map((option) => (
              <Radio key={option.value} value={option.value}>
                {option.label}
              </Radio>
            ))}
          </Radio.Group>
        </div>
      </Drawer>

      <Drawer
        className="customization-mobile-edit-drawer"
        title={null}
        placement="bottom"
        height="min(82vh, 620px)"
        footer={null}
        visible={Boolean(singleEditFields)}
        unmountOnExit
        onCancel={() => setSingleEditFields(null)}
      >
        {singleEditFields ? (
          <MobileSingleFieldEditPanel
            draft={singleEditDraft}
            demandFieldOptions={viewModel.demandFieldOptions}
            fieldOptions={viewModel.projectFieldOptions}
            projectEditOptions={viewModel.projectEditOptions}
            projectEditOptionsLoading={viewModel.projectEditOptionsLoading}
            projectPointNames={viewModel.projectPointNames}
            projectPointNamesLoading={viewModel.projectPointNamesLoading}
            fields={singleEditFields}
            saving={viewModel.saving}
            title={singleEditTitle}
            onCancel={() => setSingleEditFields(null)}
            onPointNamesSearch={viewModel.loadProjectPointNames}
            onSave={handleSaveSingleField}
            onOpenDispatcherSelection={openDispatcherSelection}
          />
        ) : null}
      </Drawer>
      <DispatcherSelectionModal
        visible={dispatcherSelectionVisible}
        selectedUserId={dispatcherSelectionInitial.userId ?? viewModel.editModel.dispatcherUserId}
        selectedDisplayName={
          dispatcherSelectionInitial.displayName ?? viewModel.editModel.dispatcherUserName
        }
        onClose={handleDispatcherSelectionClose}
        onConfirm={handleDispatcherSelectionConfirm}
      />
      <Drawer
        className="customization-mobile-confirm-drawer"
        title={`以 ${viewModel.historicalBaseVersionNumber ?? "历史版本"} 为基线生成新版本`}
        placement="bottom"
        height="auto"
        visible={historicalBaseConfirmVisible && viewModel.shouldConfirmHistoricalBaseGeneration}
        unmountOnExit
        footer={
          <div className="customization-mobile-drawer-actions">
            <Button onClick={() => setHistoricalBaseConfirmVisible(false)}>取消</Button>
            <Button
              type="primary"
              loading={viewModel.versionSubmitting}
              disabled={viewModel.versionSubmitting}
              onClick={handleConfirmHistoricalBaseGeneration}
            >
              生成新版本
            </Button>
          </div>
        }
        onCancel={() => setHistoricalBaseConfirmVisible(false)}
      >
        <div className="customization-mobile-confirm-body">
          <p>
            当前查看的是历史版本，确认后会生成新的下一版本，不会覆盖{" "}
            {viewModel.historicalBaseVersionNumber ?? "历史版本"}。
          </p>
        </div>
      </Drawer>
    </main>
  );
}

/** 手机单字段编辑面板。 */
function MobileSingleFieldEditPanel({
  draft,
  demandFieldOptions,
  fields,
  fieldOptions,
  projectEditOptions,
  projectEditOptionsLoading,
  projectPointNames,
  projectPointNamesLoading,
  saving,
  title,
  onCancel,
  onPointNamesSearch,
  onSave,
  onOpenDispatcherSelection,
}: {
  draft: CustomizationProjectUpdatePayload;
  demandFieldOptions?: CustomizationDemandFieldOptions;
  fields: MobileEditFieldKey[];
  fieldOptions?: CustomizationProjectFieldOptions;
  projectEditOptions?: CustomizationProjectEditOptions | null;
  projectEditOptionsLoading: boolean;
  projectPointNames: string[];
  projectPointNamesLoading: boolean;
  saving: boolean;
  title: string;
  onCancel: () => void;
  onPointNamesSearch: (keyword?: string) => void;
  onSave: (draft: CustomizationProjectUpdatePayload) => void;
  onOpenDispatcherSelection: (options: {
    selectedUserId?: string;
    selectedDisplayName?: string;
    onConfirm: (candidate: CustomizationDispatcherSelectionCandidate | null) => void;
  }) => void;
}) {
  const [localDraft, setLocalDraft] = useState<CustomizationProjectUpdatePayload>(draft);

  useEffect(() => {
    setLocalDraft(draft);
  }, [draft]);

  const handleLocalChange = <K extends keyof CustomizationProjectUpdatePayload>(
    key: K,
    value: CustomizationProjectUpdatePayload[K],
  ) => {
    setLocalDraft((current) => ({ ...current, [key]: value }));
  };

  return (
    <div className="customization-mobile-edit-panel" aria-label="手机单字段编辑面板">
      <div className="customization-mobile-edit-heading">
        <strong>{title || "编辑字段"}</strong>
        <span>只保存当前字段，不影响其他需求内容</span>
      </div>
      <div className="customization-mobile-edit-section is-single">
        {fields.map((field) => (
          <MobileEditFieldControl
            key={field}
            draft={localDraft}
            demandFieldOptions={demandFieldOptions}
            field={field}
            fieldOptions={fieldOptions}
            projectEditOptions={projectEditOptions}
            projectEditOptionsLoading={projectEditOptionsLoading}
            projectPointNames={projectPointNames}
            projectPointNamesLoading={projectPointNamesLoading}
            onPointNamesSearch={onPointNamesSearch}
            onChange={handleLocalChange}
            onOpenDispatcherSelection={onOpenDispatcherSelection}
          />
        ))}
      </div>
      <div className="customization-mobile-edit-actions">
        <Button type="secondary" onClick={onCancel}>
          取消
        </Button>
        <Button type="primary" loading={saving} disabled={saving} onClick={() => onSave(localDraft)}>
          保存
        </Button>
      </div>
    </div>
  );
}

/** 渲染手机编辑字段控件。 */
function MobileEditFieldControl({
  draft,
  demandFieldOptions,
  field,
  fieldOptions,
  projectEditOptions,
  projectEditOptionsLoading,
  projectPointNames,
  projectPointNamesLoading,
  onPointNamesSearch,
  onChange,
  onOpenDispatcherSelection,
}: {
  draft: CustomizationProjectUpdatePayload;
  demandFieldOptions?: CustomizationDemandFieldOptions;
  field: MobileEditFieldKey;
  fieldOptions?: CustomizationProjectFieldOptions;
  projectEditOptions?: CustomizationProjectEditOptions | null;
  projectEditOptionsLoading: boolean;
  projectPointNames: string[];
  projectPointNamesLoading: boolean;
  onPointNamesSearch: (keyword?: string) => void;
  onChange: <K extends keyof CustomizationProjectUpdatePayload>(
    key: K,
    value: CustomizationProjectUpdatePayload[K],
  ) => void;
  onOpenDispatcherSelection: (options: {
    selectedUserId?: string;
    selectedDisplayName?: string;
    onConfirm: (candidate: CustomizationDispatcherSelectionCandidate | null) => void;
  }) => void;
}) {
  const label = mobileEditFieldLabels[field];
  if (field === "primarySchoolId") {
    return (
      <MobileIdentitySelectField
        label={label}
        loading={projectEditOptionsLoading}
        options={buildIdentitySelectOptions(projectEditOptions?.collegeCandidates)}
        value={draft.primarySchoolId}
        displayValue={draft.primarySchoolName}
        onChange={(nextValue, nextLabel) => {
          onChange("primarySchoolId", nextValue);
          onChange("primarySchoolName", nextLabel);
        }}
      />
    );
  }
  if (field === "customerId") {
    return (
      <MobileIdentitySelectField
        label={label}
        loading={projectEditOptionsLoading}
        options={buildIdentitySelectOptions(projectEditOptions?.customerCandidates)}
        value={draft.customerId}
        displayValue={draft.customerName}
        onChange={(nextValue, nextLabel) => {
          onChange("customerId", nextValue);
          onChange("customerName", nextLabel);
        }}
      />
    );
  }
  if (field === "contactId") {
    return (
      <MobileIdentitySelectField
        label={label}
        loading={projectEditOptionsLoading}
        options={buildIdentitySelectOptions(projectEditOptions?.contactCandidates)}
        value={draft.contactId}
        displayValue={draft.contactName}
        onChange={(nextValue, nextLabel) => {
          onChange("contactId", nextValue);
          onChange("contactName", nextLabel);
        }}
      />
    );
  }
  if (field === "departureStartDate") {
    return (
      <MobileDateField
        label={label}
        value={draft.departureStartDate}
        onChange={(value) => onChange("departureStartDate", value)}
      />
    );
  }
  if (field === "tripType") {
    return (
      <MobileSelectField
        label={label}
        options={fieldOptions?.tripTypeOptions}
        value={draft.tripType}
        onChange={(value) => onChange("tripType", value)}
      />
    );
  }
  if (field === "signType") {
    return (
      <MobileSelectField
        label={label}
        options={fieldOptions?.signTypeOptions}
        value={draft.signType}
        onChange={(value) => onChange("signType", value)}
      />
    );
  }
  if (field === "dealProgress") {
    return (
      <MobileSelectField
        label={label}
        options={fieldOptions?.dealProgressOptions}
        value={draft.dealProgress}
        onChange={(value) => onChange("dealProgress", value)}
      />
    );
  }
  if (field === "customerType") {
    return (
      <MobileSelectField
        label={label}
        options={fieldOptions?.customerTypeOptions}
        value={draft.customerType}
        onChange={(value) => onChange("customerType", value)}
      />
    );
  }
  if (field === "dispatcherUserId") {
    return (
      <label className="customization-mobile-edit-field">
        <span>{label}</span>
        <DispatcherSelectionField
          label={label}
          value={draft.dispatcherUserId}
          displayValue={draft.dispatcherUserName}
          onOpen={() =>
            onOpenDispatcherSelection({
              selectedUserId: draft.dispatcherUserId,
              selectedDisplayName: draft.dispatcherUserName,
              onConfirm: (candidate) => {
                onChange("dispatcherUserId", candidate?.userId ?? "");
                onChange("dispatcherUserName", candidate?.displayName ?? "");
              },
            })
          }
        />
      </label>
    );
  }
  if (field === "operatingCompany") {
    return (
      <MobileSelectField
        label={label}
        options={fieldOptions?.operatingCompanyOptions}
        value={draft.operatingCompany}
        onChange={(value) => onChange("operatingCompany", value)}
      />
    );
  }
  if (field === "schoolPickupRequirement") {
    return (
      <MobileSelectField
        label={label}
        options={buildMobileSchoolPickupRequirementOptions(demandFieldOptions)}
        value={draft.schoolPickupRequirement}
        onChange={(value) => onChange("schoolPickupRequirement", value)}
      />
    );
  }
  if (mobileNumberEditFields.has(field)) {
    return (
      <MobileNumberField
        label={label}
        value={draft[field] as number | undefined}
        onChange={(value) => onChange(field, value as never)}
      />
    );
  }
  if (mobileArrayEditFields.has(field)) {
    const options =
      field === "provinces"
        ? mobileProjectEditProvinceOptions
        : field === "majorNames"
          ? buildMajorSelectOptions(projectEditOptions)
          : field === "pointNames"
            ? projectPointNames
            : field === "stayAreaNames"
              ? projectEditOptions?.stayAreaNames
              : undefined;
    const isPointNamesField = field === "pointNames";
    return (
      <MobileArrayField
        label={label}
        value={draft[field] as string[] | undefined}
        options={options}
        loading={isPointNamesField ? projectPointNamesLoading : projectEditOptionsLoading}
        allowCreate={field !== "pointNames" && field !== "stayAreaNames" && field !== "provinces"}
        preserveCurrentValues={field !== "provinces"}
        filterOption={isPointNamesField ? false : filterMobileSelectOptionByVisibleText}
        onSearch={isPointNamesField ? onPointNamesSearch : undefined}
        onVisibleChange={
          isPointNamesField
            ? (visible) => {
                if (visible) {
                  onPointNamesSearch("");
                }
              }
            : undefined
        }
        onChange={(value) => onChange(field, value as never)}
      />
    );
  }
  if (mobileBooleanEditFields.has(field)) {
    return (
      <MobileBooleanField
        label={label}
        value={draft[field] as boolean | undefined}
        trueLabel={field === "includeMeals" ? "含餐" : "包含"}
        falseLabel={field === "includeMeals" ? "不含餐" : "不包含"}
        onChange={(value) => onChange(field, value as never)}
      />
    );
  }
  if (mobileTextAreaEditFields.has(field)) {
    return (
      <MobileTextAreaField
        label={label}
        value={draft[field] as string | undefined}
        onChange={(value) => onChange(field, value as never)}
      />
    );
  }
  return (
    <MobileTextField
      label={label}
      value={draft[field] as string | undefined}
      onChange={(value) => onChange(field, value as never)}
    />
  );
}

type MobileSelectFieldProps = {
  label: string;
  value?: string;
  options?: string[];
  loading?: boolean;
  onChange: (value: string | undefined) => void;
};

/** 手机单选字段。 */
const MobileSelectField = memo(function MobileSelectField({
  label,
  value,
  options,
  loading,
  onChange,
}: MobileSelectFieldProps) {
  const selectOptions = useMemo(() => normalizeSelectOptions(value, options), [options, value]);
  return (
    <label className="customization-mobile-edit-field">
      <span>{label}</span>
      <Select
        aria-label={label}
        value={value || undefined}
        placeholder={label}
        allowClear
        allowCreate
        loading={loading}
        size="large"
        showSearch
        filterOption={filterMobileSelectOptionByVisibleText}
        onChange={(nextValue) => onChange(normalizeOptionalText(String(nextValue ?? "")))}
      >
        {selectOptions.map((option) => (
          <Select.Option key={option} value={option}>
            {option}
          </Select.Option>
        ))}
      </Select>
    </label>
  );
}, areMobileSelectFieldPropsEqual);

type MobileIdentitySelectFieldProps = {
  label: string;
  value?: string;
  displayValue?: string;
  options: MobileNativeSelectOption[];
  loading?: boolean;
  onChange: (value: string | undefined, label: string | undefined) => void;
};

/** 手机单值搜索下拉字段。 */
const MobileIdentitySelectField = memo(function MobileIdentitySelectField({
  label,
  value,
  displayValue,
  options,
  loading,
  onChange,
}: MobileIdentitySelectFieldProps) {
  const selectOptions = options.length ? options : emptyMobileIdentityOptions;
  const optionNodes = useMemo(
    () =>
      selectOptions.map((option) => (
        <Select.Option key={option.value} value={option.value}>
          {option.label}
        </Select.Option>
      )),
    [selectOptions],
  );
  const selectedValue = value || undefined;
  return (
    <label className="customization-mobile-edit-field">
      <span>{label}</span>
      <Select
        aria-label={label}
        value={selectedValue}
        placeholder={displayValue || label}
        allowClear
        loading={loading}
        size="large"
        showSearch
        filterOption={filterMobileSelectOptionByVisibleText}
        renderFormat={(option, selectValue) =>
          renderMobileIdentitySelectValue(option, selectValue, displayValue)
        }
        onChange={(nextValue) => {
          const selectedOptionValue = normalizeOptionalText(String(nextValue ?? ""));
          if (!selectedOptionValue) {
            onChange(undefined, undefined);
            return;
          }
          const matched = selectOptions.find((item) => item.value === selectedOptionValue);
          onChange(selectedOptionValue, matched?.label);
        }}
      >
        {optionNodes}
      </Select>
    </label>
  );
}, areMobileIdentitySelectFieldPropsEqual);

type MobileBooleanFieldProps = {
  label: string;
  value?: boolean;
  trueLabel: string;
  falseLabel: string;
  onChange: (value: boolean | undefined) => void;
};

/** 手机布尔选择字段。 */
const MobileBooleanField = memo(function MobileBooleanField({
  label,
  value,
  trueLabel,
  falseLabel,
  onChange,
}: MobileBooleanFieldProps) {
  const displayValue = value === undefined ? undefined : value ? "true" : "false";
  return (
    <label className="customization-mobile-edit-field">
      <span>{label}</span>
      <Select
        aria-label={label}
        value={displayValue}
        placeholder={label}
        allowClear
        size="large"
        onChange={(nextValue) => {
          const normalized = String(nextValue ?? "");
          onChange(normalized ? normalized === "true" : undefined);
        }}
      >
        <Select.Option value="true">{trueLabel}</Select.Option>
        <Select.Option value="false">{falseLabel}</Select.Option>
      </Select>
    </label>
  );
}, areMobileBooleanFieldPropsEqual);

type MobileDateFieldProps = {
  label: string;
  value?: string;
  onChange: (value: string | undefined) => void;
};

/** 手机日期字段。 */
const MobileDateField = memo(function MobileDateField({
  label,
  value,
  onChange,
}: MobileDateFieldProps) {
  return (
    <label className="customization-mobile-edit-field">
      <span>{label}</span>
      <DatePicker
        allowClear
        format="YYYY-MM-DD"
        inputProps={{ "aria-label": label }}
        placeholder="YYYY-MM-DD"
        size="large"
        value={value || undefined}
        onChange={(nextValue) => onChange(normalizeOptionalText(String(nextValue ?? "")))}
      />
    </label>
  );
}, areMobileDateFieldPropsEqual);

type MobileNumberFieldProps = {
  label: string;
  value?: number;
  onChange: (value: number | undefined) => void;
};

/** 手机数字字段。 */
const MobileNumberField = memo(function MobileNumberField({
  label,
  value,
  onChange,
}: MobileNumberFieldProps) {
  return (
    <label className="customization-mobile-edit-field">
      <span>{label}</span>
      <InputNumber
        aria-label={label}
        hideControl={false}
        min={0}
        mode="button"
        precision={0}
        size="large"
        value={value}
        onChange={(nextValue) => onChange(parseOptionalInputNumber(nextValue))}
      />
    </label>
  );
}, areMobileNumberFieldPropsEqual);

type MobileTextFieldProps = {
  label: string;
  value?: string;
  onChange: (value: string | undefined) => void;
};

/** 手机文本字段。 */
const MobileTextField = memo(function MobileTextField({
  label,
  value,
  onChange,
}: MobileTextFieldProps) {
  return (
    <label className="customization-mobile-edit-field is-wide">
      <span>{label}</span>
      <Input
        aria-label={label}
        size="large"
        value={value ?? ""}
        onChange={(nextValue) => onChange(normalizeOptionalText(nextValue))}
      />
    </label>
  );
}, areMobileTextFieldPropsEqual);

type MobileTextAreaFieldProps = {
  label: string;
  value?: string;
  onChange: (value: string | undefined) => void;
};

/** 手机多行文本字段。 */
const MobileTextAreaField = memo(function MobileTextAreaField({
  label,
  value,
  onChange,
}: MobileTextAreaFieldProps) {
  return (
    <label className="customization-mobile-edit-field is-wide">
      <span>{label}</span>
      <Input.TextArea
        aria-label={label}
        rows={4}
        value={value ?? ""}
        onChange={(nextValue) => onChange(normalizeOptionalText(nextValue))}
      />
    </label>
  );
}, areMobileTextAreaFieldPropsEqual);

type MobileArrayFieldProps = {
  label: string;
  value?: string[];
  options?: MobileNameSelectOptionInput[];
  loading?: boolean;
  allowCreate?: boolean;
  preserveCurrentValues?: boolean;
  filterOption?: false | typeof filterMobileSelectOptionByVisibleText;
  onSearch?: (keyword: string) => void;
  onVisibleChange?: (visible: boolean) => void;
  onChange: (value: string[] | undefined) => void;
};

/** 手机数组字段。 */
const MobileArrayField = memo(function MobileArrayField({
  label,
  value,
  options,
  loading,
  allowCreate,
  preserveCurrentValues = true,
  filterOption,
  onSearch,
  onVisibleChange,
  onChange,
}: MobileArrayFieldProps) {
  const normalizedValue = useMemo(() => normalizeOptionalList(value) ?? [], [value]);
  const selectOptions = useMemo(
    () =>
      normalizeMobileNameSelectOptions([
        ...(options ?? emptyMobileNameOptions),
        ...(preserveCurrentValues ? normalizedValue : emptyMobileNameOptions),
      ]),
    [normalizedValue, options, preserveCurrentValues],
  );
  const optionNodes = useMemo(
    () =>
      selectOptions.map((option) => (
        <Select.Option key={option.value} value={option.value}>
          {option.label}
        </Select.Option>
      )),
    [selectOptions],
  );
  return (
    <label className="customization-mobile-edit-field is-wide">
      <span>{label}</span>
      <Select
        aria-label={label}
        mode="multiple"
        allowClear
        allowCreate={allowCreate ?? preserveCurrentValues}
        loading={loading}
        size="large"
        showSearch
        filterOption={filterOption}
        placeholder={label}
        value={normalizedValue}
        onSearch={onSearch}
        onVisibleChange={onVisibleChange}
        onChange={(nextValue) => onChange(normalizeEditableNameList(nextValue))}
      >
        {optionNodes}
      </Select>
    </label>
  );
}, areMobileArrayFieldPropsEqual);

/** 手机需求分组。 */
function MobileDemandGroup({
  canEdit,
  disableDispatcherEdit,
  editModel,
  group,
  onEditField,
  onInlineNumberSave,
}: {
  canEdit: boolean;
  disableDispatcherEdit: boolean;
  editModel: CustomizationProjectUpdatePayload;
  group: CustomizationDemandDisplayGroup;
  onEditField: (field: CustomizationDemandDisplayField) => void;
  onInlineNumberSave: (payload: CustomizationProjectUpdatePayload) => Promise<unknown>;
}) {
  return (
    <article className="customization-mobile-demand-group">
      <header className="customization-mobile-demand-group-heading">
        <h2>{group.title}</h2>
        <span>{group.fields.length} 项</span>
      </header>
      <div className="customization-mobile-field-list">
        {group.fields.map((field) => (
          <MobileDemandField
            key={field.key}
            canEdit={canEdit}
            disableDispatcherEdit={disableDispatcherEdit}
            editModel={editModel}
            field={field}
            onEditField={onEditField}
            onInlineNumberSave={onInlineNumberSave}
          />
        ))}
      </div>
    </article>
  );
}

/** 手机需求字段。 */
function MobileDemandField({
  canEdit,
  disableDispatcherEdit,
  editModel,
  field,
  onEditField,
  onInlineNumberSave,
}: {
  canEdit: boolean;
  disableDispatcherEdit: boolean;
  editModel: CustomizationProjectUpdatePayload;
  field: MobileDemandDisplayField;
  onEditField: (field: CustomizationDemandDisplayField) => void;
  onInlineNumberSave: (payload: CustomizationProjectUpdatePayload) => Promise<unknown>;
}) {
  const value = field.displayText || field.value || "未填写";
  const editable = canEdit && getMobileDemandFieldEditKeys(field.key, { disableDispatcherEdit }).length > 0;
  const normalizedKey = field.key.trim();
  const fieldClassName = [
    "customization-mobile-field",
    editable ? "is-editable" : "",
    field.changed ? "is-changed" : "",
    field.missing ? "is-required-missing" : "",
    mobileCompactDemandFieldKeys.has(normalizedKey) ? "is-compact" : "",
    mobileDispatcherDemandFieldKeys.has(normalizedKey) ? "is-dispatcher" : "",
  ]
    .filter(Boolean)
    .join(" ");
  const inlineNumberKey = mobileInlineNumberFieldKeyByDisplayKey[normalizedKey];
  if (inlineNumberKey) {
    const hasDetailEditor = normalizedKey === "guideArrangement";
    return (
      <MobileInlineNumberDemandField
        className={fieldClassName}
        disabled={!canEdit}
        field={field}
        inputLabel={hasDetailEditor ? "领队/导游数" : field.label}
        unit={normalizedKey === "durationDays" ? "天" : "人"}
        value={editModel[inlineNumberKey] as number | undefined}
        onSave={(nextValue) => onInlineNumberSave({ [inlineNumberKey]: nextValue })}
        onEditDetails={hasDetailEditor && canEdit ? () => onEditField(field) : undefined}
      />
    );
  }
  if (editable) {
    return (
      <button
        className={fieldClassName}
        type="button"
        aria-label={`编辑 ${field.label}`}
        onClick={() => onEditField(field)}
      >
        <MobileDemandFieldLabel field={field} />
        <span className="customization-mobile-field-value">{value}</span>
        <ChevronRight size={16} />
      </button>
    );
  }
  return (
    <div className={fieldClassName}>
      <MobileDemandFieldLabel field={field} />
      <span className="customization-mobile-field-value">{value}</span>
    </div>
  );
}

const mobileInlineNumberFieldKeyByDisplayKey: Partial<
  Record<string, keyof CustomizationProjectUpdatePayload>
> = {
  durationDays: "durationDays",
  guideArrangement: "guideCount",
  studentCount: "studentCount",
  teacherCount: "teacherCount",
};

/** 手机简单数字字段直接在字段行内形成手工草稿。 */
function MobileInlineNumberDemandField({
  className,
  disabled,
  field,
  inputLabel,
  unit,
  value,
  onSave,
  onEditDetails,
}: {
  className: string;
  disabled?: boolean;
  field: MobileDemandDisplayField;
  inputLabel: string;
  unit: string;
  value?: number;
  onSave: (value: number) => Promise<unknown>;
  onEditDetails?: () => void;
}) {
  const [draftValue, setDraftValue] = useState(value === undefined ? "" : String(value));
  const [error, setError] = useState("");

  useEffect(() => {
    setDraftValue(value === undefined ? "" : String(value));
  }, [value]);

  const handleBlur = () => {
    const normalized = draftValue.trim();
    if (!/^\d+$/.test(normalized)) {
      setError("请输入不小于 0 的整数");
      return;
    }
    const nextValue = Number(normalized);
    if (!Number.isSafeInteger(nextValue) || nextValue < 0) {
      setError("请输入不小于 0 的整数");
      return;
    }
    setError("");
    if (nextValue !== value) {
      void onSave(nextValue);
    }
  };

  return (
    <div className={`${className} is-inline-number${onEditDetails ? " has-detail-action" : ""}`}>
      <MobileDemandFieldLabel field={field} />
      <span className="customization-mobile-inline-number-group">
        <input
          aria-label={inputLabel}
          className="customization-mobile-inline-number-input"
          disabled={disabled}
          inputMode="numeric"
          min={0}
          step={1}
          type="number"
          value={draftValue}
          onBlur={handleBlur}
          onChange={(event) => {
            setDraftValue(event.target.value);
            setError("");
          }}
        />
        <span>{unit}</span>
      </span>
      {onEditDetails ? (
        <button
          className="customization-mobile-inline-detail-button"
          type="button"
          aria-label="编辑 领队/导游要求"
          onClick={onEditDetails}
        >
          <ChevronRight size={16} />
        </button>
      ) : null}
      {error ? <span className="customization-mobile-inline-error">{error}</span> : null}
    </div>
  );
}

/** 手机需求字段名称，必填字段展示 PC 同款红点。 */
function MobileDemandFieldLabel({ field }: { field: MobileDemandDisplayField }) {
  if (!field.required) {
    return <span className="customization-mobile-field-label">{field.label}</span>;
  }
  return (
    <span className="customization-mobile-field-label customization-mobile-required-label">
      <span className="customization-mobile-field-label-text">{field.label}</span>
      <span className="customization-mobile-required-mark" aria-label={`${field.label}为必填项`}>
        *
      </span>
    </span>
  );
}

/** 将展示字段映射到手机可编辑字段。 */
function getMobileDemandFieldEditKeys(
  fieldKey: string,
  options: { disableDispatcherEdit?: boolean } = {},
): MobileEditFieldKey[] {
  const normalizedKey = fieldKey.trim();
  const mapping: Record<string, MobileEditFieldKey[]> = {
    destinationSummary: ["provinces"],
    customerAndContact: ["customerId", "contactId"],
    customerName: ["customerId", "contactId"],
    contactName: ["customerId", "contactId"],
    primarySchoolNameSnapshot: ["primarySchoolId"],
    majorNames: ["majorNames"],
    pointAndStayAreaNames: ["pointNames", "stayAreaNames"],
    dateAndPeople: ["departureStartDate"],
    departureStartDate: ["departureStartDate"],
    durationDays: ["durationDays"],
    studentCount: ["studentCount"],
    teacherCount: ["teacherCount"],
    provinces: ["provinces", "stayAreaNames", "pointNames"],
    stayAreaNames: ["provinces", "stayAreaNames", "pointNames"],
    pointNames: ["provinces", "stayAreaNames", "pointNames"],
    budgetPerPerson: ["budgetPerPerson"],
    businessStatus: ["customerType", "tripType", "signType", "dealProgress"],
    executionTeam: ["dispatcherUserId", "operatingCompany"],
    dispatcherUserName: ["dispatcherUserId"],
    teachingRequirements: ["teachingRequirements"],
    specialRequirements: ["specialRequirements"],
    schoolPickupRequirement: ["schoolPickupRequirement"],
    invoiceRequirement: ["invoiceRequirement"],
    remark: ["remark"],
    teacherArrangement: [
      "teacherMealRequirement",
      "teacherTicketRequirement",
      "teacherTransportRequirement",
      "teacherAccommodationRequirement",
    ],
    guideArrangement: ["guideNote"],
    ticketAndMeal: ["ticketNote", "mealNote"],
    trafficAndAccommodation: ["roundTripTransportNote", "localTransportNote", "accommodationNote"],
    visitArrangement: [
      "companyVisitCount",
      "companyVisitNote",
      "intangibleHeritageCount",
      "intangibleHeritageNote",
    ],
  };
  const fields = mapping[normalizedKey] ?? [];
  if (!options.disableDispatcherEdit) {
    return fields;
  }
  return fields.filter((field) => field !== "dispatcherUserId");
}

/** 拾取本次需要保存的手机编辑字段。 */
function pickMobileEditFields(
  draft: CustomizationProjectUpdatePayload,
  fields: MobileEditFieldKey[],
): CustomizationProjectUpdatePayload {
  const result: CustomizationProjectUpdatePayload = {};
  const relatedNameFields: Partial<Record<MobileEditFieldKey, keyof CustomizationProjectUpdatePayload>> = {
    contactId: "contactName",
    customerId: "customerName",
    dispatcherUserId: "dispatcherUserName",
    primarySchoolId: "primarySchoolName",
  };
  fields.forEach((field) => {
    if (Object.prototype.hasOwnProperty.call(draft, field) && draft[field] !== undefined) {
      result[field] = draft[field] as never;
    }
    const nameField = relatedNameFields[field];
    if (
      nameField &&
      draft[field] !== undefined &&
      Object.prototype.hasOwnProperty.call(draft, nameField)
    ) {
      result[nameField] = draft[nameField] as never;
    }
  });
  return result;
}

/** 格式化版本必填状态。 */
function formatRequiredStatus(version: CustomizationDemandVersionOption): string {
  const reasons = version.needsHumanReviewReasons ?? [];
  if (reasons.some((reason) => reason.includes("缺少") || reason.includes("待补"))) {
    return "缺必填";
  }
  if (reasons.length > 0) {
    return "待确认";
  }
  return "完整";
}

/** 顶部通知按钮：已填写计调时直接标明通知对象。 */
function formatNotifyDispatcherLabel(dispatcherName: string): string {
  const normalizedName = dispatcherName.trim();
  return normalizedName && normalizedName !== "未指定计调" ? `通知计调${normalizedName}` : "通知计调";
}

/** 按手机字段筛选保留非空分组。 */
function filterMobileDemandGroups(
  groups: CustomizationDemandDisplayGroup[],
  filter: MobileDemandFieldFilter,
  manualDraftPatch: CustomizationProjectUpdatePayload | null,
  versionChangedFieldKeys: string[],
): CustomizationDemandDisplayGroup[] {
  if (filter === "all") {
    return groups;
  }
  const versionChangedSet = new Set(versionChangedFieldKeys);
  return groups
    .map((group) => ({
      ...group,
      fields: group.fields.filter((field) => {
        if (filter === "required") {
          return Boolean((field as MobileDemandDisplayField).required);
        }
        if (filter === "version") {
          return versionChangedSet.has(field.key);
        }
        return isMobileDemandFieldInManualDraft(field.key, manualDraftPatch);
      }),
    }))
    .filter((group) => group.fields.length > 0);
}

/** 判断展示字段是否命中当前未提交手工草稿。 */
function isMobileDemandFieldInManualDraft(
  fieldKey: string,
  manualDraftPatch: CustomizationProjectUpdatePayload | null,
): boolean {
  if (!manualDraftPatch) {
    return false;
  }
  const directField = mobileInlineNumberFieldKeyByDisplayKey[fieldKey];
  const editFields = directField ? [directField] : getMobileDemandFieldEditKeys(fieldKey);
  return editFields.some((key) => Object.prototype.hasOwnProperty.call(manualDraftPatch, key));
}

/** 解释生成行程和通知计调的共同门禁。 */
function resolveMobilePostVersionDisabledReason({
  confirmedTraceCount,
  hasManualDraft,
  requiredStatus,
  requiredStatusText,
}: {
  confirmedTraceCount: number;
  hasManualDraft: boolean;
  requiredStatus: CustomizationMobileWorkbenchViewModel["persistedRequiredStatus"]["status"];
  requiredStatusText: string;
}): string {
  if (hasManualDraft || confirmedTraceCount > 0) {
    return "请先提交当前草稿和已确认追溯，再生成行程或通知计调。";
  }
  if (requiredStatus !== "complete") {
    return requiredStatusText;
  }
  return "";
}

/** 构造手机确认栏的一行状态。 */
function resolveMobileConfirmationStatus(
  viewModel: CustomizationMobileWorkbenchViewModel,
  confirmedTraceCount: number,
): string {
  if (viewModel.versionSeq > 1 && viewModel.requiredStatus.status !== "complete") {
    return `当前 V${viewModel.versionSeq} 存在异常缺项，不能覆盖历史版本。`;
  }
  if (!viewModel.hasManualDraft && confirmedTraceCount === 0) {
    return "当前没有待提交的需求变化。";
  }
  return `${viewModel.versionSubmitLabel} 将包含当前草稿和已确认追溯。`;
}

/** 构造手机端通知计调模板。 */
function buildMobileNotifyActionOptions({
  actorName,
  versionNumber,
}: {
  actorName?: string;
  versionNumber?: string;
}): Array<{ label: string; value: string }> {
  const normalizedVersion = versionNumber?.trim() || "当前版本";
  const normalizedActorName = actorName?.trim();
  if (!normalizedActorName) {
    return [];
  }
  return [
    `${normalizedActorName}已修改版本 ${normalizedVersion}`,
    `${normalizedActorName}已创建版本 ${normalizedVersion}，请你做行程`,
  ].map((value) => ({ label: value, value }));
}

/** 解析手机通知模板里可直接展示的登录人姓名，未加载时不返回占位值。 */
function resolveMobileNotificationActorName(user: AuthUser | null | undefined): string {
  return (
    user?.displayName?.trim() ||
    user?.realname?.trim() ||
    user?.nickname?.trim() ||
    user?.username?.trim() ||
    ""
  );
}

/** 规整移动编辑提交数据。 */
function normalizeMobileEditPayload(
  draft: CustomizationProjectUpdatePayload,
): CustomizationProjectUpdatePayload {
  const result: CustomizationProjectUpdatePayload = { ...draft };
  const textKeys: Array<keyof CustomizationProjectUpdatePayload> = [
    "primarySchoolName",
    "customerName",
    "contactName",
    "dispatcherUserName",
    "budgetPerPerson",
    "customerType",
    "dealProgress",
    "departureStartDate",
    "destinationSummary",
    "invoiceRequirement",
    "operatingCompany",
    "schoolPickupRequirement",
    "signType",
    "specialRequirements",
    "teachingRequirements",
    "tripType",
    "remark",
    "teacherMealRequirement",
    "teacherTicketRequirement",
    "teacherTransportRequirement",
    "teacherAccommodationRequirement",
    "roundTripTransportNote",
    "localTransportNote",
    "accommodationNote",
    "guideNote",
    "ticketNote",
    "mealNote",
    "companyVisitNote",
    "intangibleHeritageNote",
  ];
  const arrayKeys: Array<keyof CustomizationProjectUpdatePayload> = [
    "provinces",
    "majorNames",
    "pointNames",
    "stayAreaNames",
    "mealStandards",
  ];
  textKeys.forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(result, key)) {
      const normalized = normalizeOptionalText(result[key] as string | undefined);
      if (normalized === undefined) {
        delete result[key];
      } else {
        result[key] = normalized as never;
      }
    }
  });
  arrayKeys.forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(result, key)) {
      const normalized = normalizeOptionalList(result[key] as string[] | undefined);
      if (normalized === undefined) {
        delete result[key];
      } else {
        result[key] = normalized as never;
      }
    }
  });
  return result;
}

/** 生成单选项，保留当前值。 */
function normalizeSelectOptions(value?: string, options?: string[]): string[] {
  const items = [value, ...(options ?? [])]
    .map((item) => item?.trim() ?? "")
    .filter(Boolean);
  return Array.from(new Set(items));
}

/** 构建手机接送要求枚举，保持和 PC 编辑候选一致。 */
function buildMobileSchoolPickupRequirementOptions(
  options?: CustomizationDemandFieldOptions,
): string[] {
  return options?.schoolPickupRequirementOptions?.length
    ? options.schoolPickupRequirementOptions
    : fallbackMobileSchoolPickupRequirementOptions;
}

/** 构建单值候选。 */
function buildIdentitySelectOptions(
  candidates?:
    | CustomizationProjectEditOptions["collegeCandidates"]
    | CustomizationProjectEditOptions["customerCandidates"]
    | CustomizationProjectEditOptions["contactCandidates"],
): MobileNativeSelectOption[] {
  return (candidates ?? []).map((item) => ({
    value: item.id,
    label: item.name,
  }));
}

/** 构建专业候选，展示名称与分类。 */
function buildMajorSelectOptions(options?: CustomizationProjectEditOptions | null): MobileNativeSelectOption[] {
  return (options?.majorCandidates ?? []).map((item) => ({
    value: item.name,
    label: [item.name, item.category].filter(Boolean).join(" / "),
  }));
}

/** ID 型候选未加载完成时优先使用中文名称回显。 */
function renderMobileIdentitySelectValue(
  option: { children?: ReactNode } | null,
  value: unknown,
  fallbackLabel?: string,
): ReactNode {
  if (option?.children !== undefined && option.children !== null && option.children !== "") {
    return option.children;
  }
  const fallbackText = fallbackLabel?.trim();
  if (fallbackText) {
    return fallbackText;
  }
  if (value && typeof value === "object") {
    const labeledValue = value as { label?: ReactNode; value?: unknown };
    if (labeledValue.label !== undefined && labeledValue.label !== null && labeledValue.label !== "") {
      return labeledValue.label;
    }
    return labeledValue.value === undefined || labeledValue.value === null ? "" : String(labeledValue.value);
  }
  return value === undefined || value === null ? "" : String(value);
}

/** 让 Arco Option 按可见文本搜索，而不是只按底层 value/ID。 */
function filterMobileSelectOptionByVisibleText(inputValue: string, option: ReactElement): boolean {
  const keyword = inputValue.trim().toLocaleLowerCase();
  if (!keyword) {
    return true;
  }
  const props = option.props as {
    children?: ReactNode;
    value?: string | number;
    extra?: { label?: ReactNode };
  };
  return [
    stringifyMobileSelectFilterText(props.children),
    stringifyMobileSelectFilterText(props.extra?.label),
    stringifyMobileSelectFilterText(props.value),
  ]
    .join(" ")
    .toLocaleLowerCase()
    .includes(keyword);
}

/** 提取 Arco Option 可搜索文本。 */
function stringifyMobileSelectFilterText(value: unknown): string {
  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }
  if (Array.isArray(value)) {
    return value.map((item) => stringifyMobileSelectFilterText(item)).join(" ");
  }
  if (value && typeof value === "object" && "props" in value) {
    return stringifyMobileSelectFilterText(
      (value as ReactElement<{ children?: unknown }>).props.children,
    );
  }
  return "";
}

/** 规整名称候选，结果只跟候选源有关，避免输入时重算大列表。 */
function normalizeMobileNameSelectOptions(options: MobileNameSelectOptionInput[]): MobileNativeSelectOption[] {
  const result: MobileNativeSelectOption[] = [];
  const usedValues = new Set<string>();
  for (const option of options) {
    const nextOption = normalizeMobileNameSelectOption(option);
    if (nextOption.value && !usedValues.has(nextOption.value)) {
      usedValues.add(nextOption.value);
      result.push(nextOption);
    }
  }
  return result;
}

/** 规整单个名称候选。 */
function normalizeMobileNameSelectOption(option: MobileNameSelectOptionInput): MobileNativeSelectOption {
  if (typeof option === "string") {
    return { value: option, label: option };
  }
  return option;
}

/** 规整可选文本。 */
function normalizeOptionalText(value?: string): string | undefined {
  const normalized = value?.trim();
  return normalized || undefined;
}

/** 规整可选数组。 */
function normalizeOptionalList(value?: string[]): string[] | undefined {
  const items = value?.map((item) => item.trim()).filter(Boolean) ?? [];
  return items.length > 0 ? Array.from(new Set(items)) : undefined;
}

/** 规整 Arco 多选返回值。 */
function normalizeEditableNameList(value: unknown): string[] | undefined {
  if (Array.isArray(value)) {
    return normalizeOptionalList(value.map((item) => String(item)));
  }
  if (typeof value === "string") {
    return normalizeOptionalList(
      value
        .split(/[、,，\s\n]+/)
        .map((item) => item.trim()),
    );
  }
  return undefined;
}

/** 规整 Arco 数字输入值。 */
function parseOptionalInputNumber(value: unknown): number | undefined {
  if (value === null || value === undefined || value === "") {
    return undefined;
  }
  const result = Number(value);
  return Number.isFinite(result) ? result : undefined;
}

/** 手机端版本提交成功后使用轻量消息反馈。 */
function buildMobileVersionSuccessMessage(
  actionLabel: string,
  versionNumber?: string,
): string {
  const normalizedVersion = versionNumber?.trim() || "当前版本";
  if (actionLabel.startsWith("更新")) {
    return `${normalizedVersion} 更新成功`;
  }
  if (actionLabel === "生成第一版") {
    return `${normalizedVersion} 生成成功`;
  }
  return `${normalizedVersion} 创建成功`;
}

/** 比较普通单选字段渲染所需属性。 */
function areMobileSelectFieldPropsEqual(
  previous: MobileSelectFieldProps,
  next: MobileSelectFieldProps,
): boolean {
  return (
    previous.label === next.label &&
    previous.value === next.value &&
    previous.options === next.options &&
    previous.loading === next.loading
  );
}

/** 比较 ID 型搜索单选字段渲染所需属性。 */
function areMobileIdentitySelectFieldPropsEqual(
  previous: MobileIdentitySelectFieldProps,
  next: MobileIdentitySelectFieldProps,
): boolean {
  return (
    previous.label === next.label &&
    previous.value === next.value &&
    previous.displayValue === next.displayValue &&
    previous.options === next.options &&
    previous.loading === next.loading
  );
}

/** 比较布尔下拉字段渲染所需属性。 */
function areMobileBooleanFieldPropsEqual(
  previous: MobileBooleanFieldProps,
  next: MobileBooleanFieldProps,
): boolean {
  return (
    previous.label === next.label &&
    previous.value === next.value &&
    previous.trueLabel === next.trueLabel &&
    previous.falseLabel === next.falseLabel
  );
}

/** 比较日期字段渲染所需属性。 */
function areMobileDateFieldPropsEqual(previous: MobileDateFieldProps, next: MobileDateFieldProps): boolean {
  return previous.label === next.label && previous.value === next.value;
}

/** 比较数字字段渲染所需属性。 */
function areMobileNumberFieldPropsEqual(
  previous: MobileNumberFieldProps,
  next: MobileNumberFieldProps,
): boolean {
  return previous.label === next.label && previous.value === next.value;
}

/** 比较文本字段渲染所需属性。 */
function areMobileTextFieldPropsEqual(previous: MobileTextFieldProps, next: MobileTextFieldProps): boolean {
  return previous.label === next.label && previous.value === next.value;
}

/** 比较多行文本字段渲染所需属性。 */
function areMobileTextAreaFieldPropsEqual(
  previous: MobileTextAreaFieldProps,
  next: MobileTextAreaFieldProps,
): boolean {
  return areMobileTextFieldPropsEqual(previous, next);
}

/** 比较数组字段渲染所需属性。 */
function areMobileArrayFieldPropsEqual(
  previous: MobileArrayFieldProps,
  next: MobileArrayFieldProps,
): boolean {
  return (
    previous.label === next.label &&
    previous.options === next.options &&
    previous.loading === next.loading &&
    previous.allowCreate === next.allowCreate &&
    previous.preserveCurrentValues === next.preserveCurrentValues &&
    previous.filterOption === next.filterOption &&
    previous.onSearch === next.onSearch &&
    previous.onVisibleChange === next.onVisibleChange &&
    areStringArraysEqual(previous.value ?? [], next.value ?? [])
  );
}

/** 比较字符串数组。 */
function areStringArraysEqual(previous: string[], next: string[]): boolean {
  return previous.length === next.length && previous.every((item, index) => item === next[index]);
}
